import { EventType } from '@oneclick/analytics-sdk';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useAnalytics from '../../../../hooks/useAnalytics';
import { createHeaders, getApiUrls } from '../../../services/serviceConfig';
import ChatButton from '../../atoms/ChatButton/ChatButton';
import RecommendationCard from './RecommendationCard';
import './StudentRecommendations.scss';

const StudentRecommendations = ({ className }) => {
  const { t } = useTranslation(['chatbot']);
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const history = useHistory();
  const cardsContainerRef = useRef(null);
  const { trackEvent } = useAnalytics();

  // Function to calculate activities completed based on session data
  const calculateActivitiesCompleted = (session) => {
    return session.activityInstances.filter((activity) => activity.respondedAt).length;
  };

  // Function to map backend session types to frontend challenge types
  const mapSessionTypeToChallenge = (sessionType) => {
    const typeMap = {
      NEW: 'new',
      PRACTICE: 'review',
    };

    return typeMap[sessionType] || 'new';
  };

  // Function to prepare subtitle maintaining HTML except <p> tags
  const prepareLessonsSubtitle = (lessons) => {
    if (!lessons || lessons.length === 0) {
      return '';
    }

    // Function to remove only <p> tags but keep other HTML
    const removePTags = (html) => {
      return html.replace(/<\/?p[^>]*>/gi, '').trim();
    };

    // Extract lesson titles removing only <p> tags
    const lessonTitles = lessons.map((lesson) => removePTags(lesson.title)).filter((title) => title);

    if (lessonTitles.length === 0) {
      return '';
    }

    // Join all titles with comma, wrap in <p> tags
    return `<p>${lessonTitles.join(', ')}</p>`;
  };

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.getSessions(), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (response.ok) {
        const sessions = await response.json();

        if (sessions.length === 0) {
          setRecommendations([]);
        } else {
          const mappedRecommendations = sessions.map((session) => {
            const activitiesCompleted = calculateActivitiesCompleted(session);

            // Obtener las traducciones de títulos de tarjetas como objeto
            const cardTitles = t('student_recommendations_card_titles', { returnObjects: true });

            // Acceder directamente a la traducción usando la clave del session
            const titleTranslation =
              typeof cardTitles === 'object' && cardTitles && session.sessionTitle
                ? cardTitles[session.sessionTitle] || session.sessionTitle
                : session.sessionTitle;

            // Prepare subtitle from lessons
            const subtitleForLessons = prepareLessonsSubtitle(session.lessons);

            return {
              id: session.id,
              title: titleTranslation,
              subtitle: subtitleForLessons,
              icon: '📊',
              challengeType: mapSessionTypeToChallenge(session.sessionType),
              totalActivities: session.maxActivityNumber,
              activitiesCompleted,
              completed: session.status === 'COMPLETED',
              sessionData: session,
            };
          });

          setRecommendations(mappedRecommendations);
        }
      } else {
        console.error('Failed to fetch recommendations: HTTP', response.status);
        setRecommendations([]);
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      setRecommendations([]);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleRecommendationClick = (recommendation) => {
    // Navigate directly using the session data that's already available
    const { sessionData } = recommendation;

    if (sessionData && sessionData.activityInstances && sessionData.activityInstances.length > 0) {
      // Send analytics event for activity session started
      trackEvent(EventType.ACTIVITY_SESSION_STARTED, {
        practice_session_id: sessionData.id,
        type: recommendation.challengeType, // "new", "review", etc.
        subject: sessionData.subject, // "Mathematics", "Language", etc.
      });

      // Get the last activity instance (highest index/order)
      const lastActivity = sessionData.activityInstances[sessionData.activityInstances.length - 1];

      // Navigate using the standard template literal pattern
      history.push(`/activities/sessions/${sessionData.id}/activities/${lastActivity.id}`);
    } else {
      console.error('No activity instances found for session:', recommendation.id);
    }
  };

  const handleGenerateSessions = async () => {
    setIsLoading(true);
    try {
      const urls = getApiUrls();
      console.log('Session generation request:', urls.activities.generateSession());
      const response = await fetch(urls.activities.generateSession(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({}),
      });

      console.log('Session generation response:', response);

      if (response.ok) {
        const data = await response.json();
        console.log('Session generation response:', data); // Debug log

        // Reload recommendations using the same logic that works on page load
        await fetchRecommendations();
      } else {
        console.error('Failed to generate sessions: HTTP', response.status);
      }
    } catch (error) {
      console.error('Failed to generate sessions:', error);
      // Keep existing recommendations on error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={clsx('student-recommendations', className)}>
      <div className="student-recommendations__header">
        <div className="student-recommendations__header-left">
          <div className="student-recommendations__avatar" />
          <div className="student-recommendations__header-content">
            <h2 className="student-recommendations__title">{t('Recommendations')}</h2>
            {recommendations.length > 0 ? (
              <p className="student-recommendations__subtitle">{t('Based on your performance')}</p>
            ) : (
              <p className="student-recommendations__subtitle">{t('Great job!')}</p>
            )}
          </div>
        </div>
        <ChatButton onClick={handleGenerateSessions} disabled={isLoading} icon="refresh" variant="contained" size="small">
          {t('Generate')}
        </ChatButton>
      </div>

      {(isLoading || isInitialLoading) && (
        <div className="student-recommendations__cards">
          <div className="student-recommendations__loader">{t('Loading')}...</div>
        </div>
      )}

      {!isLoading && !isInitialLoading && recommendations.length > 0 && (
        <div className="student-recommendations__cards" ref={cardsContainerRef}>
          {recommendations.map((recommendation, index) => (
            <RecommendationCard
              key={recommendation.id || index}
              recommendation={recommendation}
              onClick={handleRecommendationClick}
              containerRef={cardsContainerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

StudentRecommendations.propTypes = {
  className: PropTypes.string,
};

StudentRecommendations.defaultProps = {
  className: '',
};

export default StudentRecommendations;
